import React from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 140px 0;
  background: #FFFDF7;
  @media (min-width: 768px) { padding: 180px 0; }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Eyebrow = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  color: #C07020;
  margin-bottom: 48px;
`;

const Grid = styled.div`
  display: grid;
  gap: 0;
  border-top: 1px solid rgba(26,18,8,0.1);
  @media (min-width: 768px) { grid-template-columns: 1fr 1fr; }
`;

const Entry = styled.div`
  padding: 56px 0;
  border-bottom: 1px solid rgba(26,18,8,0.08);
  transition: opacity 0.9s ease, transform 0.9s ease;
  transition-delay: ${({ $delay }) => $delay}ms;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(20px);`}
  @media (min-width: 768px) {
    padding: 64px 56px 64px 0;
    border-right: 1px solid rgba(26,18,8,0.08);
    &:nth-child(even) {
      padding-left: 56px;
      padding-right: 0;
      border-right: none;
    }
  }
`;

const EntryLabel = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  color: rgba(26,18,8,0.4);
  margin-bottom: 20px;
  text-transform: uppercase;
`;

const EntryName = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 400;
  color: #1A1208;
  letter-spacing: -0.01em;
  margin-bottom: 28px;
  line-height: 1.15;
`;

const Detail = styled.p`
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(26,18,8,0.55);
  line-height: 1.8;
  margin-bottom: 6px;
  a {
    color: inherit;
    transition: color 0.2s;
    &:hover { color: #7C3A00; }
  }
`;

const distributors = [
  {
    name: 'La Silla Wholesale LLC',
    address: '2643 Brenner Dr, Dallas, TX 75220',
    phone: '(469) 828-1872',
    type: { en: 'U.S. Distributor', es: 'Distribuidor EE. UU.' },
  },
  {
    name: 'DRA Fiesta Foods LLC',
    address: '11223 Rush St S El Monte Unit G, CA 91733',
    phone: '+1 (323) 517-4027',
    type: { en: 'U.S. Distributor', es: 'Distribuidor EE. UU.' },
  },
];

export function Distributors() {
  const { t } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.2 });

  return (
    <Section id="distribuidores" aria-label="Distribuidores EE. UU.">
      <Container ref={ref} $inView={isInView}>
        <Eyebrow>{t('Our U.S. Distributors', 'Nuestros Distribuidores en EE. UU.')}</Eyebrow>
        <Grid>
          {distributors.map((d, i) => (
            <Entry key={i} $inView={isInView} $delay={i * 150}>
              <EntryLabel>{t(d.type.en, d.type.es)}</EntryLabel>
              <EntryName>{d.name}</EntryName>
              <Detail>{d.address}</Detail>
              <Detail>
                <a href={`tel:${d.phone.replace(/[^0-9+]/g, '')}`}>{d.phone}</a>
              </Detail>
            </Entry>
          ))}
        </Grid>
      </Container>
    </Section>
  );
}
